import { loadRemote, registerRemotes } from "@module-federation/enhanced/runtime";
import { ACL_SESSION_STORAGE, IS_ACL_ENABLED, IS_ROUTE_ENABLED } from "component/utils/Constant";
import React, { memo, useEffect, useState } from "react";
import { setBeURL, setEnvFrom, setIsHost } from "store/AppGlobalStore";
import { getEnvVariableValue, waitForEnvReady } from "util/CommonUtil";
import { ENV_VAR } from "util/Constant";
import App from "./App";
import { initI18n } from "./i18n";
import * as serviceWorker from "./serviceWorker";
import "./styles/custom-primereact.css";
import "./styles/index.css";
//
import "asset/custom.css";
import "asset/prosemirror-custom.css";

interface IAppInitializerProps {
  isHost?: boolean;
  remoteName?: string;
  basePath?: string; // Base path from host - if provided, will be used instead of calculating from isHost/remoteName
  beURL?: string;
}

const AppInitializer: React.FC<IAppInitializerProps> = (props: IAppInitializerProps) => {
  const { isHost = false, remoteName = "", basePath = "", beURL = "" } = props;
  const [isInitialized, setIsInitialized] = useState(false);
  const [isHostRegistered, setIsHostRegistered] = useState(false);
  const containerId = getEnvVariableValue("VITE_REMOTE_NAME") || "enterprisegpt";

  useEffect(() => {
    // temporary i18n initialization
    initI18n(isHost).catch((error) => {
      console.error("Failed to initialize i18n:", error);
    });

    // Wait for environment variables to be ready before initializing
    waitForEnvReady().then(() => {
      // to hide console in prod
      if (getEnvVariableValue(ENV_VAR.VITE_SHOW_LOGS) === "false") console.log = () => {};

      // configure withAuth Hoc
      if (getEnvVariableValue(ENV_VAR.VITE_IS_ACL_ENABLED) === IS_ACL_ENABLED.Y) {
        sessionStorage.setItem(ACL_SESSION_STORAGE.IS_ACL_ENABLED, IS_ACL_ENABLED.Y);
      }

      if (getEnvVariableValue(ENV_VAR.VITE_IS_ROUTE_GUARD) === IS_ROUTE_ENABLED.Y) {
        sessionStorage.setItem(ACL_SESSION_STORAGE.IS_ROUTE_GUARD, IS_ROUTE_ENABLED.Y);
      }

      setIsInitialized(true);

      // Service Worker setup
      if (getEnvVariableValue("VITE_ENABLE_PWA_MODE")) {
        // to enable PWA mode uncomment the following
        serviceWorker.register({
          onSuccess: () => {
            console.log("SW >>> SUCCESSFULLY REGISTERED!!!");
          },
          onUpdate: (reg: any) => {
            console.log("SW >>> NEW UPDATE IS AVAILABLE!!!");
          },
        });
      } else {
        // standard mode service workers are off i.e not PWA mode
        serviceWorker.unregister();
      }

      // Initialize HyperDX once (commented out, but can be enabled if needed)
      // try {
      //   if (!(window as any).__HYPER_DX_INITIALIZED__) {
      //     HyperDX.init({
      //       url: getEnvVariableValue("VITE_HYPERDX_API_URL") || "",
      //       apiKey: getEnvVariableValue("VITE_HYPERDX_API_KEY") || "",
      //       service: "synthia-frontend",
      //       tracePropagationTargets: [/api\.myapp\.domain/i],
      //       consoleCapture: true,
      //       advancedNetworkCapture: true,
      //       captureErrors: true,
      //       enableSessionReplay: true,
      //     });
      //     (window as any).__HYPER_DX_INITIALIZED__ = true;

      //     // If user data is already available (e.g., returning user), set global attributes once
      //     try {
      //       // Prefer USER_DATA as per login flow; fallback to USER_INFO
      //       const userDataRaw = JSON.parse(getLocalStorage(LOCAL_STORAGE.USER_DATA) || "{}");
      //       if (userDataRaw) {
      //         const userId = userDataRaw?.userId || userDataRaw?.id || userDataRaw?.uid;
      //         const userEmail = userDataRaw?.email || "";
      //         const userName = userDataRaw?.userName || userDataRaw?.name || "";
      //         if (userId || userEmail || userName) {
      //           HyperDX.setGlobalAttributes({
      //             userId,
      //             userEmail,
      //             userName,
      //             teamName: "vm",
      //           });
      //         }
      //       }
      //     } catch (e) {
      //       // no-op
      //     }
      //   }
      // } catch (e) {
      //   // no-op
      // }
    });
  }, []);

  async function loadHostAssets(hostBaseUrl: string) {
    try {
      const manifestUrl = `${hostBaseUrl}/manifest-assets.json`;
      const res = await fetch(manifestUrl);
      console.log("res", res);
      if (!res.ok) throw new Error(`Failed to fetch manifest from ${manifestUrl}`);
      const manifest = await res.json();
      // Get CSS assets from the new structure
      const cssFiles = manifest.css?.sync ?? [];
      // Filter only the ones starting with "assets/index"
      const indexCssFiles = cssFiles.filter((file: string) => file.startsWith("assets/index"));
      // Inject each CSS file once
      for (const cssFile of indexCssFiles) {
        const fullUrl = `${hostBaseUrl}/${cssFile}`;
        console.log("fullUrl", fullUrl);
        if (!document.querySelector(`link[href="${fullUrl}"]`)) {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = fullUrl;
          document.head.appendChild(link);
        }
      }
    } catch (err) {
      console.error("❌ Failed to load host CSS:", err);
    }
  }

  useEffect(() => {
    setIsHost(!!isHost);
    setEnvFrom(remoteName);
    setBeURL(beURL);

    const registerAndLoadHost = async () => {
      try {
        // Fetch remote config from remotes.json
        // const res = await fetch("/remotes.json", { cache: "no-store" });
        // const json = await res.json();
        // const remoteConfigs = Array.isArray(json?.remotes) ? json.remotes : [];

        // if (remoteConfigs.length === 0) {
        //   throw new Error("No remotes found in remotes.json");
        // }

        const hostRemote = {
          name: "host",
          entry:
            "https://viamagus-host." + getEnvVariableValue(ENV_VAR.BASE_DOMAIN) + "/remoteEntry.js",
          type: "module",
        };
        // Register remotes
        registerRemotes([hostRemote]);

        // Mark host as registered so HostComponentsProvider can render
        setIsHostRegistered(true);

        // Initialize i18n based on isHost parameter
        initI18n(isHost).catch((error) => {
          console.error("Failed to initialize i18n:", error);
        });

        // Find and load the host remote

        if (!hostRemote) {
          throw new Error("Host remote not found in remotes.json");
        }

        // Load host styles using loadRemote
        try {
          const stylesResponse: any = await loadRemote("host/styles");
          console.log("Host styles loaded:", stylesResponse);
        } catch (error) {
          console.warn("Failed to load host styles:", error);
        }

        // Load host assets (CSS from manifest)
        let baseForEnv = hostRemote.entry;
        try {
          const u = new URL(hostRemote.entry);
          baseForEnv = `${u.protocol}//${u.host}`;
        } catch (_e) {
          baseForEnv = (hostRemote.entry || "")
            .replace(/\/+remoteEntry\.js$/i, "")
            .replace(/\/$/, "");
        }
        loadHostAssets(baseForEnv);
      } catch (error: any) {
        console.error("❌ Error registering/loading host remote:", error);
        // Still mark as registered even on error so app can render
        setIsHostRegistered(true);
        // Fallback to direct URL approach if remotes.json fails
        const hostBaseUrl =
          process.env.NODE_ENV === "production"
            ? "https://vm-host.synthia.dev.vm.web-devapp.com"
            : "http://localhost:4174";
        loadHostAssets(hostBaseUrl);
      }
    };

    //registerAndLoadHost();
  }, [isHost, remoteName]);

  // if (!isInitialized) {
  //   return null; // or a loading component
  // }

  // if (!isHostRegistered) {
  //   return null; // Wait for host registration
  // }

  return (
    <div id={containerId} className="h-full w-full min-w-0 overflow-hidden">
      <div id={`errorDiv${containerId}`} className="fixed top-0 z-[1000] w-full" />
      <div
        id={`notificationBannerTop${containerId}`}
        className="sticky z-50"
        style={{ top: "0px" }}
      />
      <App isHost={isHost} remoteName={remoteName} basePath={basePath} />
      <div
        id={`notificationBannerBottom${containerId}`}
        className="sticky z-40"
        style={{ bottom: "0px" }}
      />
    </div>
  );
};

export default memo(AppInitializer);
